import { formFormat } from '@/domain/common/format'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { BalanceType } from '@/features/dialogs/common/types'
import { AssetInput, AssetInputProps } from '@/ui/molecules/asset-input/AssetInput'
import { Control, Controller, useFormContext } from 'react-hook-form'

interface ControlledMultiSelectorAssetInputProps {
  fieldName: string
  control: Control<any>
  token: Token
  max?: NormalizedUnitNumber
  maxSelectedFieldName?: string
  onRemove?: () => void
  balance?: NormalizedUnitNumber
  balanceType?: BalanceType
  disabled?: boolean
  showError?: boolean // defaults to show error if field is touched or dirty
  variant?: AssetInputProps['variant']
  walletIconLabel?: string
  resetBorrowStatus?: () => void
  customOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSetMax?: (max: NormalizedUnitNumber) => void
  readOnly?: boolean
  className?: string
}

export function ControlledMultiSelectorAssetInput({
  fieldName,
  control,
  disabled,
  token,
  onRemove,
  balance,
  balanceType,
  max,
  maxSelectedFieldName,
  showError,
  variant,
  walletIconLabel,
  readOnly,
  resetBorrowStatus,
  customOnChange,
  onSetMax,
  className,
}: ControlledMultiSelectorAssetInputProps) {
  const { setValue, trigger, getValues } = useFormContext()

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field, fieldState: { error, isTouched, isDirty } }) => {
        showError = showError ?? (isTouched || isDirty)
        const isMaxSelected = maxSelectedFieldName ? getValues(maxSelectedFieldName) : false

        function toggleIsMaxSelected() {
          if (maxSelectedFieldName) {
            setValue(maxSelectedFieldName, !isMaxSelected, {
              shouldValidate: true,
            })
          }
        }

        const setMaxValue = max?.gt(0)
          ? () => {
              setValue(fieldName, formFormat(max, token.decimals), {
                shouldValidate: true,
              })
              if (onSetMax) {
                onSetMax(max)
              }
              toggleIsMaxSelected()
            }
          : undefined

        return (
          <AssetInput
            {...field}
            token={token}
            onRemove={onRemove}
            balance={balance}
            balanceType={balanceType}
            readOnly={readOnly}
            error={showError ? error?.message : undefined}
            disabled={disabled && !resetBorrowStatus}
            variant={variant}
            walletIconLabel={walletIconLabel}
            setMax={setMaxValue}
            isMaxSelected={isMaxSelected}
            className={className}
            autoComplete="off"
            onChange={(e) => {
              field.onChange(e)
              if (disabled) {
                resetBorrowStatus?.()
              }
              if (maxSelectedFieldName && max) {
                const currentValue = NormalizedUnitNumber(e.target.value || '0')
                const maxValue = max
                const isCurrentlyMax = currentValue.eq(maxValue)
                setValue(maxSelectedFieldName, isCurrentlyMax, {
                  shouldValidate: true,
                })
              }
              if (customOnChange) {
                customOnChange(e)
              }
              // always trigger validation of the whole form
              trigger().catch(console.error)
            }}
          />
        )
      }}
    />
  )
}
